import React, { useState } from 'react';

import { AuthTags } from '~constants/authTags';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { AlertList, Button, ErrorResponse, Spinner } from '~components';
import { TableView } from '~components/table-view';

import formatDate from '~support-at-home/lib/formatDate';
import formatDateOnly from '~support-at-home/lib/formatDateOnly';

import {
  registeredServicesColumns,
  registeredServicesMinTableWidth,
} from '~support-at-home/forms/proda/registeredServicesColumns';

import {
  useGetSahProviderSummaryQuery,
  useRefreshSahProviderSummaryMutation,
} from '~support-at-home/actions/sahProviderSummary';

const SPINNER_SIZE = 16;

const SahRegisteredServices: React.FC = () => {
  const { data, isLoading, isError, error } = useGetSahProviderSummaryQuery();
  const [refresh] = useRefreshSahProviderSummaryMutation();
  const { showToast } = useToast();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleRefresh = async () => {
    if (isRefreshing) return; // debounce: no re-entry while a refresh is in flight
    setIsRefreshing(true);
    setErrorMessage(null);
    try {
      // Read the 200 body rather than assuming success, the same way SahReferenceDataManager does.
      // A refresh that lost the advisory-lock race returns success: true with a message saying it
      // was superseded and nothing was written, so a hardcoded "refreshed" would be a lie.
      const result = await refresh().unwrap();
      showToast('success', result.message || 'Registered services refreshed');
    } catch (err) {
      const message = getErrorMessage(err);
      setErrorMessage(message);
      showToast('danger', message);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) return <Spinner loading className="p-5" />;
  if (isError) return <ErrorResponse message={getErrorMessage(error)} />;

  // A never-synced company returns the same shape with every summary scalar null and both arrays
  // empty — not an error, not a 404. `last_api_sync_at` is the marker for that state.
  const notYetSynced = !data?.last_api_sync_at;
  const registeredServiceTypes = data?.registered_service_types ?? [];
  const registrationCategories = data?.registration_categories ?? [];

  return (
    <div data-cy="sah-registered-services">
      {errorMessage && <ErrorResponse message={errorMessage} />}
      {!!data?.warnings?.length && (
        <AlertList
          header="Services Australia registration mismatch"
          color="warning"
          listItems={data.warnings}
        />
      )}
      <div className="d-flex justify-content-between align-items-end mb-2">
        <div>
          {notYetSynced ? (
            <p className="text-muted mb-0">
              Not yet synced. Refresh to fetch your registration from Services Australia.
            </p>
          ) : (
            <>
              <p className="mb-0">
                <strong>{data?.service_provider_name}</strong> ({data?.service_provider_id})
              </p>
              <small className="text-muted">Last synced: {formatDate(data?.last_api_sync_at)}</small>
            </>
          )}
        </div>
        <Button
          color="success"
          size="sm"
          authTag={AuthTags.SUPPORTATHOME.PRODA.MANAGE}
          onClick={handleRefresh}
          disabled={isRefreshing}
          loading={isRefreshing}
          spinnerProps={{ loading: true, type: 'clip', size: SPINNER_SIZE }}
          aria-label="Refresh registered services"
          data-cy="sah-registered-services-refresh-btn"
        >
          Refresh
        </Button>
      </div>
      {!notYetSynced && (
        <TableView
          id="sah-registered-services"
          data={registeredServiceTypes}
          columns={registeredServicesColumns}
          readonly
          compactView
          minimalView
          minTableWidth={registeredServicesMinTableWidth}
          bordered
          emptyMessage="No registered services found. Refresh to fetch from Services Australia."
        />
      )}
      {!!registrationCategories.length && (
        <div className="mt-3">
          <h6>Registration Categories</h6>
          <ul className="mb-0">
            {registrationCategories.map((category, index) => (
              <li key={`${category.categoryCode}-${index}`}>
                {category.categoryText} ({formatDateOnly(category.startDate)} - {formatDateOnly(category.endDate)})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SahRegisteredServices;
